import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './ColorPicker.css';

const ColorPicker = ({ 
  label, 
  value, 
  onChange, 
  placeholder, 
  description,
  cssVariables = [],
  className = '',
  disabled = false 
}) => {
  // Отладка: проверяем наличие CSS переменных
  // console.log('ColorPicker received cssVariables:', cssVariables, 'for label:', label);
  
  const [localValue, setLocalValue] = useState(value || '');
  const [showPicker, setShowPicker] = useState(false);
  const [portalPos, setPortalPos] = useState(null);
  const groupRef = useRef(null);
  const pickerRef = useRef(null);

  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  const normalizeHex = useCallback((input) => {
    let v = (input || '').trim();
    if (v === '') return '';
    if (v[0] !== '#') v = `#${v}`;
    v = `#${v.slice(1).replace(/[^0-9a-fA-F]/g, '')}`;
    if (v.length > 7) v = v.slice(0, 7);
    return v;
  }, []);

  const isValidHex = useCallback((hex) => {
    return /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(hex);
  }, []);

  const handleHexChange = useCallback((e) => {
    const normalized = normalizeHex(e.target.value);
    setLocalValue(normalized);
    if (isValidHex(normalized)) {
      onChange(normalized);
    }
  }, [normalizeHex, isValidHex, onChange]);

  const handleHexBlur = useCallback(() => {
    const normalized = normalizeHex(localValue);
    setLocalValue(normalized);
    if (isValidHex(normalized)) {
      onChange(normalized);
    }
  }, [localValue, normalizeHex, isValidHex, onChange]);

  const handleColorPickerChange = useCallback((e) => {
    const hex = e.target.value;
    setLocalValue(hex);
    onChange(hex);
  }, [onChange]);

  const togglePicker = useCallback(() => {
    if (disabled) return;
    
    if (!showPicker && groupRef.current) {
      const rect = groupRef.current.getBoundingClientRect();
      setPortalPos({ 
        left: rect.left, 
        top: rect.bottom + 8, 
        width: rect.width 
      });
    }
    setShowPicker(prev => !prev);
  }, [showPicker, disabled]);

  const handlePickerCommit = useCallback((hex) => {
    onChange(hex);
    setShowPicker(false);
  }, [onChange]);

  // Закрытие пикера при клике вне его
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target) && 
          groupRef.current && !groupRef.current.contains(event.target)) {
        setShowPicker(false);
      }
    };

    if (showPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showPicker]);

  // Проверяем наличие CSS переменных
  const hasCssVars = cssVariables && Array.isArray(cssVariables) && cssVariables.length > 0;
  const firstCssVar = hasCssVars ? cssVariables[0] : null;
  
  // Функция копирования CSS переменной в буфер обмена
  const handleCopyCssVar = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (firstCssVar) {
      try {
        await navigator.clipboard.writeText(firstCssVar);
        // Можно добавить уведомление о копировании
        // NotificationManager.success(`Скопировано: ${firstCssVar}`);
      } catch (err) {
        // Fallback для старых браузеров
        const textArea = document.createElement('textarea');
        textArea.value = firstCssVar;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
        } catch (fallbackErr) {
          // Ошибка копирования в старых браузерах - игнорируем
        }
        document.body.removeChild(textArea);
      }
    }
  }, [firstCssVar]);
  
  return (
    <div className={`color-picker-wrapper ${className}`}>
      <label className="color-picker-label">
        <span className="color-picker-label-text">{label}</span>
        {firstCssVar && (
          <span 
            className="color-picker-css-var-inline" 
            title={`CSS переменная: ${firstCssVar}. Кликните, чтобы скопировать в буфер обмена`}
            onClick={handleCopyCssVar}
          >
            {firstCssVar}
          </span>
        )}
        {description && <span className="color-picker-description">{description}</span>}
        {cssVariables && cssVariables.length > 1 && (
          <span className="color-picker-css-vars" title="Все CSS переменные, которые изменяются этим полем">
            🎨 Все переменные: {cssVariables.join(', ')}
          </span>
        )}
      </label>
      
      <div className="color-picker-group" ref={groupRef}>
        <div className="color-picker-inputs">
          <input
            type="color"
            value={localValue || '#ffffff'}
            onChange={handleColorPickerChange}
            className="color-picker-native"
            disabled={disabled}
            title={`Выберите цвет для ${label.toLowerCase()}`}
          />
          
          <input
            type="text"
            value={localValue}
            onChange={handleHexChange}
            onBlur={handleHexBlur}
            className="color-picker-text"
            placeholder={placeholder}
            disabled={disabled}
            title={`Введите hex-код цвета (например: ${placeholder})`}
          />
        </div>
        
        <button
          type="button"
          className="color-picker-toggle"
          onClick={togglePicker}
          disabled={disabled}
          title={showPicker ? 'Скрыть палитру' : 'Показать палитру'}
        >
          <span className="color-picker-icon">🎨</span>
          <span className="color-picker-text-btn">
            {showPicker ? 'Скрыть' : 'Палитра'}
          </span>
        </button>
      </div>

      {showPicker && groupRef.current && portalPos && createPortal(
        <div
          ref={pickerRef}
          className="color-picker-portal"
          style={{
            position: 'fixed',
            left: portalPos.left,
            top: portalPos.top,
            zIndex: 2000,
          }}
        >
          <AdvancedColorPicker
            value={localValue || '#ffffff'}
            onLive={(hex) => setLocalValue(hex)}
            onCommit={handlePickerCommit}
          />
        </div>,
        document.body
      )}

      {showPicker && (
        <UpdatePortalPosition 
          anchorRef={groupRef} 
          onPos={(pos) => setPortalPos(pos)} 
        />
      )}
    </div>
  );
};

// Компонент для обновления позиции портала
const UpdatePortalPosition = ({ anchorRef, onPos }) => {
  useEffect(() => {
    const update = () => {
      const el = anchorRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      onPos({ left: rect.left, top: rect.bottom + 8, width: rect.width });
    };
    
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [anchorRef, onPos]);
  
  return null;
};

// Продвинутый цветовой пикер
const AdvancedColorPicker = ({ value, onLive, onCommit }) => {
  const [hsv, setHsv] = useState(() => hexToHsv(value || '#ffffff'));
  
  useEffect(() => {
    const newHsv = hexToHsv(value || '#ffffff');
    setHsv(newHsv);
  }, [value]);

  const handleHsvChange = useCallback((newHsv) => {
    setHsv(newHsv);
    if (onLive) {
      onLive(hsvToHex(newHsv));
    }
  }, [onLive]);

  const handleCommit = useCallback(() => {
    if (onCommit) {
      onCommit(hsvToHex(hsv));
    }
  }, [hsv, onCommit]);

  return (
    <div className="advanced-color-picker">
      <div className="color-picker-header">
        <span className="color-picker-title">Выбор цвета</span>
        <div 
          className="color-picker-preview"
          style={{ backgroundColor: hsvToHex(hsv) }}
        />
      </div>
      
      <div className="color-picker-content">
        <SVSquare
          hue={hsv.h}
          s={hsv.s}
          v={hsv.v}
          onChange={({ s, v }) => handleHsvChange({ ...hsv, s, v })}
          onDone={handleCommit}
        />
        <HueSlider
          hue={hsv.h}
          onChange={(h) => handleHsvChange({ ...hsv, h })}
          onDone={handleCommit}
        />
      </div>
      
      <div className="color-picker-footer">
        <button 
          type="button" 
          className="color-picker-apply"
          onClick={handleCommit}
        >
          Применить
        </button>
      </div>
    </div>
  );
};

// Компонент для выбора насыщенности и яркости
const SVSquare = ({ hue, s, v, onChange, onDone }) => {
  const ref = useRef(null);
  const [drag, setDrag] = useState(false);

  const clamp = (num, min, max) => Math.min(Math.max(num, min), max);

  const update = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    
    const rect = el.getBoundingClientRect();
    const cx = (e.clientX || (e.touches && e.touches[0].clientX) || 0) - rect.left;
    const cy = (e.clientY || (e.touches && e.touches[0].clientY) || 0) - rect.top;
    
    onChange({ 
      s: clamp(cx / rect.width, 0, 1), 
      v: clamp(1 - cy / rect.height, 0, 1) 
    });
  }, [onChange]);

  const handlePointerDown = useCallback((e) => {
    e.preventDefault();
    setDrag(true);
    document.body.style.overflow = 'hidden';
    update(e);
    if (e.currentTarget.setPointerCapture) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
  }, [update]);

  const handlePointerMove = useCallback((e) => {
    if (drag) update(e);
  }, [drag, update]);

  const handlePointerUp = useCallback((e) => {
    if (!drag) return;
    setDrag(false);
    document.body.style.overflow = '';
    update(e);
    if (onDone) onDone();
  }, [drag, update, onDone]);

  const handlePointerCancel = useCallback(() => {
    if (!drag) return;
    setDrag(false);
    document.body.style.overflow = '';
  }, [drag]);

  return (
    <div
      ref={ref}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      className="sv-square"
      style={{
        background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, ${hsvToHex({ h: hue, s: 1, v: 1 })})`
      }}
    >
      <div 
        className="sv-square-handle"
        style={{ 
          left: `${s * 100}%`, 
          top: `${(1 - v) * 100}%` 
        }} 
      />
    </div>
  );
};

// Компонент для выбора оттенка
const HueSlider = ({ hue, onChange, onDone }) => {
  const ref = useRef(null);
  const [drag, setDrag] = useState(false);

  const clamp = (num, min, max) => Math.min(Math.max(num, min), max);

  const update = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    
    const rect = el.getBoundingClientRect();
    const x = clamp((e.clientX || (e.touches && e.touches[0].clientX) || 0) - rect.left, 0, rect.width);
    onChange(clamp((x / rect.width) * 360, 0, 360));
  }, [onChange]);

  const handlePointerDown = useCallback((e) => {
    e.preventDefault();
    setDrag(true);
    document.body.style.overflow = 'hidden';
    update(e);
    if (e.currentTarget.setPointerCapture) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
  }, [update]);

  const handlePointerMove = useCallback((e) => {
    if (drag) update(e);
  }, [drag, update]);

  const handlePointerUp = useCallback((e) => {
    if (!drag) return;
    setDrag(false);
    document.body.style.overflow = '';
    update(e);
    if (onDone) onDone();
  }, [drag, update, onDone]);

  const handlePointerCancel = useCallback(() => {
    if (!drag) return;
    setDrag(false);
    document.body.style.overflow = '';
  }, [drag]);

  return (
    <div
      ref={ref}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      className="hue-slider"
    >
      <div 
        className="hue-slider-handle"
        style={{ left: `${(hue / 360) * 100}%` }} 
      />
    </div>
  );
};

// Утилиты для работы с цветами
const hexToRgb = (hex) => {
  let v = (hex || '#ffffff').replace('#', '');
  if (v.length === 3) v = v.split('').map((c) => c + c).join('');
  const n = parseInt(v || 'ffffff', 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};

const rgbToHex = ({ r, g, b }) => `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;

const rgbToHsv = ({ r, g, b }) => {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn), d = max - min;
  let h = 0;
  if (d !== 0) {
    switch (max) {
      case rn: h = ((gn - bn) / d + (gn < bn ? 6 : 0)); break;
      case gn: h = (bn - rn) / d + 2; break;
      default: h = (rn - gn) / d + 4; break;
    }
    h /= 6;
  }
  const s = max === 0 ? 0 : d / max;
  const v = max;
  return { h: h * 360, s, v };
};

const hsvToRgb = ({ h, s, v }) => {
  const c = v * s;
  const hp = (h / 60) % 6;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r1 = 0, g1 = 0, b1 = 0;
  if (0 <= hp && hp < 1) { r1 = c; g1 = x; b1 = 0; }
  else if (1 <= hp && hp < 2) { r1 = x; g1 = c; b1 = 0; }
  else if (2 <= hp && hp < 3) { r1 = 0; g1 = c; b1 = x; }
  else if (3 <= hp && hp < 4) { r1 = 0; g1 = x; b1 = c; }
  else if (4 <= hp && hp < 5) { r1 = x; g1 = 0; b1 = c; }
  else { r1 = c; g1 = 0; b1 = x; }
  const m = v - c;
  return { r: Math.round((r1 + m) * 255), g: Math.round((g1 + m) * 255), b: Math.round((b1 + m) * 255) };
};

const hsvToHex = (hsv) => rgbToHex(hsvToRgb(hsv));
const hexToHsv = (hex) => rgbToHsv(hexToRgb(hex));

export default ColorPicker;
